import React, { useEffect, useRef } from 'react';

const CosmicBg = ({ accentColor = '#c2ffe0', isDark = true, pageBg }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;

    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    resize();

    const hex2rgb = (hex) => {
      const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return r ? [parseInt(r[1], 16), parseInt(r[2], 16), parseInt(r[3], 16)] : [194, 255, 224];
    };
    const [ar, ag, ab] = hex2rgb(accentColor);

    // ✅ Use pageBg prop
    const bg = pageBg || (isDark ? '#030308' : '#f5f0ff');

    const STARS = Array.from({ length: 120 }, () => ({
      x: Math.random(), y: Math.random(),
      size: Math.random() * 2,
      phase: Math.random() * Math.PI * 2,
      speed: 0.005 + Math.random() * 0.01,
    }));

    const DUST = Array.from({ length: 30 }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0003,
      vy: (Math.random() - 0.5) * 0.0003,
      size: 40 + Math.random() * 80,
      alpha: 0.03 + Math.random() * 0.07,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      DUST.forEach((d) => {
        d.x += d.vx; d.y += d.vy;
        if (d.x < -0.1) d.x = 1.1;
        if (d.x > 1.1) d.x = -0.1;
        if (d.y < -0.1) d.y = 1.1;
        if (d.y > 1.1) d.y = -0.1;

        const px = d.x * canvas.width, py = d.y * canvas.height;
        const g = ctx.createRadialGradient(px, py, 0, px, py, d.size * canvas.width * 0.001);
        g.addColorStop(0, `rgba(${ar},${ag},${ab},${isDark ? d.alpha : d.alpha * 0.5})`);
        g.addColorStop(1, `rgba(${ar},${ag},${ab},0)`);
        ctx.beginPath();
        ctx.arc(px, py, d.size * canvas.width * 0.001, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      });

      STARS.forEach((s) => {
        s.phase += s.speed;
        const px = s.x * canvas.width, py = s.y * canvas.height;
        const flicker = 0.4 + 0.6 * Math.abs(Math.sin(s.phase));
        const alpha = flicker * (isDark ? 0.85 : 0.4);
        ctx.beginPath();
        ctx.arc(px, py, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ar},${ag},${ab},${alpha})`;
        ctx.fill();
        if (s.size > 1.2) {
          const g = ctx.createRadialGradient(px, py, 0, px, py, s.size * 4);
          g.addColorStop(0, `rgba(${ar},${ag},${ab},${flicker * (isDark ? 0.3 : 0.15)})`);
          g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.beginPath();
          ctx.arc(px, py, s.size * 4, 0, Math.PI * 2);
          ctx.fillStyle = g;
          ctx.fill();
        }
      });

      for (let i = 0; i < 3; i++) {
        const nx = canvas.width * (0.2 + i * 0.3);
        const ny = canvas.height * (0.3 + Math.sin(Date.now() * 0.0001 + i) * 0.1);
        const g = ctx.createRadialGradient(nx, ny, 0, nx, ny, canvas.width * 0.25);
        g.addColorStop(0, `rgba(${ar},${ag},${ab},${isDark ? 0.04 : 0.03})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.beginPath();
        ctx.ellipse(nx, ny, canvas.width * 0.25, canvas.height * 0.15, i * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = g;
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    draw();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(raf); };
  }, [accentColor, isDark, pageBg]);

  return <canvas ref={canvasRef} className="fixed inset-0 z-0 pointer-events-none" />;
};

export default CosmicBg;